<template>
  <div class="videoSeek_trim-root" @mousedown="onMouseDownRoot">
    <div
      id="videoSeek_trim-root"
      ref="root"
      :style="rootStyleHasValue && rootStyle"
      class="videoSeek_trim"
      :class="rootTrimClass"
    >
      <span
        id="videoSeek_trim-right"
        class="videoSeek_trim-right"
        @mousedown="onMouseDownRoot"
      >
        <span
          id="videoSeek_trim-right-arrow"
          class="videoSeek_trim-right-arrow"
          :class="{
            'videoSeek_trim-right-arrow-unselect': selectedElement !== 'right',
          }"
        />
      </span>
      <span
        id="videoSeek_trim-left"
        class="videoSeek_trim-left"
        @mousedown="onMouseDownRoot"
      >
        <span
          id="videoSeek_trim-left-arrow"
          class="videoSeek_trim-left-arrow"
          :class="{
            'videoSeek_trim-left-arrow-unselect': selectedElement !== 'left',
          }"
        />
      </span>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { getTranslateXY } from '@/utils/coordinates';
import { convertDateToPixel, naiveRound } from '@/utils/helpers';
import dayjs from 'dayjs';

export default {
  name: 'VideoSeekTrim',
  props: {
    initEndDate: {
      type: Number,
      default: null,
    },
    startRange: {
      type: Number,
      default: null,
    },
    endRange: {
      type: Number,
      default: null,
    },
    durationFactor: {
      type: Number,
      default: 1,
    },
    realDuration: {
      type: Object,
      default: null,
    },
    timelineWidth: {
      type: Number,
      default: 64,
    },
    isLive: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isMouseDownRoot: false,
      percent: -50,
      startX: 0,
      startTranslateX: 0,
      startLeftX: 0,
      startRightX: 0,
      startRootWidth: 0,
      selectedElement: null,
      clicked: false,
      resizeObserver: null,
      rootStyle: {},
      maxPointInterval: null,
      realTrimWidth: 35,
      realTrimDuration: 0,
      currentSelectionRange: 239,
    };
  },
  computed: {
    rootStyleHasValue() {
      return Object.keys(this.rootStyle).length > 0;
    },
    isMemoryTimeExceed() {
      return dayjs().isAfter(this.endRange);
    },
    rootTrimClass() {
      return 'videoSeek_trim--' + this.durationFactor;
    },
    minSelectionRange() {
      switch (this.durationFactor) {
        case 3:
        case 6:
        case 12:
          return (this.timelineWidth - 60) / 30;
        case 24:
          return (this.timelineWidth - 60) / 40;
        case 1:
        default:
          return (this.timelineWidth - 60) / 45;
      }
    },
  },
  watch: {
    timelineWidth() {
      if (this.currentSelectionRange < this.minSelectionRange) {
        this.$set(this.rootStyle, 'width', `${this.minSelectionRange}px`);
      }
    },
    durationFactor: {
      handler: function (currentValue) {
        this.$nextTick(() => {
          // Based on the logic of 1/23/24, when the trim bar is placed in the (1, 3)-hours zones
          // The width will be fixed using css.
          if ([1, 3].includes(currentValue) && this.isLive) {
            const { root } = this.$refs;
            const rootRect = root.getBoundingClientRect();
            const parentClient =
              root.parentElement.parentElement.getBoundingClientRect();
            const indicatorPoint =
              root.parentElement.parentElement.clientWidth / 2;
            const currTrimStartLeft = rootRect.left - parentClient.x;
            const pointBeyondIndicator =
              root.offsetWidth - (indicatorPoint - currTrimStartLeft);

            this.$set(
              this.rootStyle,
              'left',
              `${root.offsetLeft - pointBeyondIndicator}px`
            );
          }
        });
      },
      deep: false,
    },
  },
  created() {
    this.debounceResize = debounce(this.onResize, 100);
  },
  mounted() {
    const { root } = this.$refs;
    const { parentElement } = root;
    this.resizeObserver = new ResizeObserver(this.debounceResize).observe(
      parentElement.parentElement
    );
    window.addEventListener('mouseup', this.onMouseUpRoot);
    window.addEventListener('mousemove', this.onMouseMoveRoot);
    this.setInitPoints();
    this.$nextTick(() => {
      this.getTimeFromPixel();
    });
    if (this.maxPointInterval) {
      clearInterval(this.maxPointInterval);
    }
    this.maxPointInterval = setInterval(() => {
      this.getMaxPoint();
    }, 1000);
    window.addEventListener('resize', this.handleWindowResize);
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.$refs.root);
    }
    clearInterval(this.maxPointInterval);
    window.removeEventListener('mouseup', this.onMouseUpRoot);
    window.removeEventListener('mousemove', this.onMouseMoveRoot);
    window.removeEventListener('resize', this.handleWindowResize);
  },
  methods: {
    setInitPoints() {
      const { root } = this.$refs;
      if (this.initEndDate) {
        const parentClient =
          root.parentElement.parentElement.getBoundingClientRect();
        const trimEndPoint = convertDateToPixel(
          this.initEndDate,
          this.startRange,
          this.endRange,
          parentClient.width
        );
        const border = 3;
        let initialFactor;
        switch (this.durationFactor) {
          case 1:
            initialFactor = 0;
            break;
          case 3:
            initialFactor = 50;
            break;
          default:
            // Plus 5 units because when selecting a time zone above 6, the ruler is offset by a few px
            // Causing the left attribute of the "rootStyle" variable to display incorrectly.
            initialFactor = 105;
            break;
        }
        this.$set(
          this.rootStyle,
          'left',
          `${naiveRound(
            trimEndPoint -
              root.clientWidth +
              parentClient.x -
              border -
              initialFactor,
            3
          )}px`
        );
      }
    },
    getMaxPoint() {
      const { root } = this.$refs;
      const parentClient =
        root.parentElement.parentElement.getBoundingClientRect();
      return convertDateToPixel(
        dayjs(),
        this.startRange,
        this.endRange,
        parentClient.width
      );
    },
    getTimeFromPixel() {
      const { root } = this.$refs;
      const borderSize = 3;
      const rootRect = root.getBoundingClientRect();
      const parentClient =
        root.parentElement.parentElement.getBoundingClientRect();
      const start = rootRect.left - parentClient.x;
      const end = rootRect.right - parentClient.x;
      this.realTrimWidth = end - start;
      this.$emit('onABChange', {
        start: start + borderSize,
        end: end - borderSize,
        parentWidth: parentClient.width,
        movePoint: this.selectedElement,
        minRange: this.currentSelectionRange <= this.minSelectionRange,
      });
    },
    getRealTrimDuration() {
      if (this.realDuration) {
        const startDate = dayjs(this.realDuration.start).format(
          'YYYYMMDDHHmmss'
        );
        const endDate = dayjs(this.realDuration.end).format('YYYYMMDDHHmmss');
        return dayjs(endDate).diff(dayjs(startDate), 'minutes');
      }
      return 0;
    },
    onResize() {},
    onMouseDownRoot(e) {
      e.stopPropagation();
      if (e.target.id === 'videoSeek_trim-root') {
        this.selectedElement = 'root';
      } else if (
        ['videoSeek_trim-right', 'videoSeek_trim-right-arrow'].includes(
          e.target.id
        )
      ) {
        this.selectedElement = 'right';
      } else if (
        ['videoSeek_trim-left', 'videoSeek_trim-left-arrow'].includes(
          e.target.id
        )
      ) {
        this.selectedElement = 'left';
      } else {
        this.selectedElement = null;
      }
      const { root } = this.$refs;
      this.isMouseDownRoot = true;
      this.startX = e.clientX;
      this.startTranslateX = getTranslateXY(root).x;
      const rootRect = root.getBoundingClientRect();
      const parentClient =
        root.parentElement.parentElement.getBoundingClientRect();
      this.startLeftX = rootRect.left - parentClient.x;
      this.startRightX = rootRect.right - parentClient.x;
      this.startRootWidth = root.clientWidth;
      if (!this.clicked) {
        this.$set(this.rootStyle, 'transform', 'unset');
        this.$set(this.rootStyle, 'left', `${this.startLeftX}px`);
        this.clicked = true;
      }
    },
    onMouseUpRoot() {
      this.isMouseDownRoot = false;
      if (this.realTrimDuration > 30) {
        const { root } = this.$refs;
        let initializeMaxWidth =
          (30 * (root.clientWidth + 5)) / this.getRealTrimDuration();
        // This temporarily calculates maxWidth of a selected durationFactor
        // and set its minimum width
        if (this.minSelectionRange >= initializeMaxWidth) {
          initializeMaxWidth = this.minSelectionRange;
        }
        this.$set(this.rootStyle, 'width', `${initializeMaxWidth}px`);
        this.$set(this.rootStyle, 'maxWidth', `${initializeMaxWidth}px`);
        this.realTrimDuration = 30;
      }
    },
    onMouseMoveRoot(e) {
      if (this.isMouseDownRoot) {
        const { root } = this.$refs;
        const { parentElement } = root.parentElement;
        const { clientX } = e;
        const rootBorderSize = 6;
        let newLeft = this.startLeftX + (clientX - this.startX);
        const maxPoint = naiveRound(this.getMaxPoint(), 3);
        this.$set(this.rootStyle, 'transform', 'unset');
        this.getTimeFromPixel(root, parentElement);
        if (this.selectedElement === 'root') {
          if (newLeft < 0) {
            newLeft = 0;
          }
          if (
            newLeft + root.clientWidth > parentElement.clientWidth &&
            this.isMemoryTimeExceed
          ) {
            newLeft = parentElement.clientWidth - root.clientWidth - 4;
          } else if (newLeft + root.clientWidth > maxPoint - 4) {
            newLeft = maxPoint - 4 - root.clientWidth;
          }
          this.$set(this.rootStyle, 'left', `${newLeft}px`);
        } else if (this.selectedElement === 'right') {
          let newWidth =
            this.startRootWidth + (clientX - this.startX) + rootBorderSize;
          if (
            this.startLeftX + newWidth > parentElement.clientWidth &&
            this.isMemoryTimeExceed
          ) {
            newWidth = parentElement.clientWidth - this.startLeftX;
          } else if (this.startLeftX + newWidth > maxPoint) {
            newWidth = maxPoint - this.startLeftX;
          }
          if (newWidth < this.minSelectionRange) {
            newWidth = this.minSelectionRange;
          }
          this.currentSelectionRange = newWidth;
          this.$set(this.rootStyle, 'width', `${newWidth}px`);
        } else if (this.selectedElement === 'left') {
          let newWidth =
            this.startRootWidth + (this.startX - clientX) + rootBorderSize;
          if (newLeft < 0) {
            newLeft = 0;
            newWidth = this.startRightX;
          }
          if (newWidth >= this.minSelectionRange) {
            this.$set(this.rootStyle, 'left', `${newLeft}px`);
            this.$set(this.rootStyle, 'width', `${newWidth}px`);
          } else if (newWidth < this.minSelectionRange) {
            newWidth = this.minSelectionRange;
          }
          this.currentSelectionRange = newWidth;
        }
        // Set maximum video clip creation time to 30 minutes with its width
        this.realTrimDuration = this.getRealTrimDuration();
        if (this.realTrimDuration >= 30) {
          this.$set(this.rootStyle, 'maxWidth', `${this.realTrimWidth}px`);
        } else {
          this.$set(this.rootStyle, 'maxWidth', 'unset');
        }
      }
    },
    handleWindowResize() {
      this.$set(this.rootStyle, 'width', '35px');
      this.$set(this.rootStyle, 'maxWidth', 'unset');
    },
  },
};
</script>

<style lang="scss">
.videoSeek_trim {
  $circle_size: 13px;
  $clip_color: #37a34a;
  &-root {
    width: 100%;
    height: 100%;
  }
  z-index: 3;
  position: absolute;
  top: -4.3px;
  width: 35px;
  height: 28px;
  transform: translateX(-50%);
  left: 50%;
  //padding: 11px 5px 5px 2.4px;
  border: solid 3px $clip_color;
  &--1 {
    width: 239px;
  }
  &--3 {
    width: 170px;
  }
  &-right,
  &-left {
    height: $circle_size * 2;
    width: 18px;
    background-color: $clip_color;
    position: absolute;
    &-arrow {
      width: 0;
      height: 0;
      position: inherit;
    }
  }
  &-right {
    cursor: e-resize;
    top: -2px;
    right: -18px;
    border-bottom-right-radius: $circle_size * 2;
    border-top-right-radius: $circle_size * 2;
    &-arrow {
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-right: 8px solid #ffffff;
      top: 7px;
      left: 4px;
      &-unselect {
        &:before {
          content: '';
          width: 0;
          height: 0;
          position: absolute;
          border-top: 4px solid transparent;
          border-bottom: 4px solid transparent;
          border-right: 6px solid #37a34a;
          top: -4px;
          left: 1px;
        }
      }
    }
  }
  &-left {
    cursor: w-resize;
    top: -2px;
    left: -18px;
    border-bottom-left-radius: $circle_size * 2;
    border-top-left-radius: $circle_size * 2;
    &-arrow {
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-left: 8px solid #ffffff;
      top: 7px;
      right: 4px;
      &-unselect {
        &:before {
          content: '';
          width: 0;
          height: 0;
          position: absolute;
          border-top: 4px solid transparent;
          border-bottom: 4px solid transparent;
          border-left: 6px solid #37a34a;
          top: -4px;
          right: 1px;
        }
      }
    }
  }
}
</style>
